import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { fetchPageMention } from './api'

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const PAGE_PATHS = [
  { kind: 'advice', pattern: new RegExp(`^/advices/(${UUID})/?$`, 'i') },
  { kind: 'legalChange', pattern: new RegExp(`^/legal-changes/(${UUID})/?$`, 'i') }
]

// The advice or legal change a URL to this app points to, or null.
// Only links to the app's own origin count, so pasting a link to another
// RegulApp environment stays a plain link.
export const parsePageUrl = (text, origin) => {
  let url
  try {
    url = new URL((text || '').trim())
  } catch (error) {
    return null
  }
  if (url.origin !== origin) {
    return null
  }
  for (const { kind, pattern } of PAGE_PATHS) {
    const match = url.pathname.match(pattern)
    if (match) {
      return { kind, id: match[1].toLowerCase() }
    }
  }
  return null
}

// Pasting a bare link to an advice or legal change turns it into a page
// mention. The link is inserted as is first, then replaced once the page's
// title has loaded; if the page can't be loaded (no access, deleted) the
// plain link stays.
export const PageLinkPaste = Extension.create({
  name: 'pageLinkPaste',

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('pageLinkPaste'),
        props: {
          handlePaste: (view, event) => {
            const clipboard = event.clipboardData
            const text = ((clipboard && clipboard.getData('text/plain')) || '').trim()
            const page = parsePageUrl(text, window.location.origin)
            const { schema, selection } = view.state
            if (!page || !schema.marks.link || selection.$from.parent.type.spec.code) {
              return false
            }

            const from = selection.from
            const link = schema.text(text, [schema.marks.link.create({ href: text })])
            view.dispatch(view.state.tr.replaceSelectionWith(link, false))
            trackAndReplace(editor, from, from + text.length, text, page)
            return true
          }
        }
      })
    ]
  }
})

// Follows the pasted link's range through later edits until the page has
// loaded, then swaps it for a mention if it's still the untouched link.
const trackAndReplace = (editor, initialFrom, initialTo, url, page) => {
  let from = initialFrom
  let to = initialTo
  let deleted = false
  const onTransaction = ({ transaction }) => {
    const start = transaction.mapping.mapResult(from, 1)
    const end = transaction.mapping.mapResult(to, -1)
    deleted = deleted || start.deleted || end.deleted
    from = start.pos
    to = end.pos
  }
  editor.on('transaction', onTransaction)

  const finish = (mention) => {
    editor.off('transaction', onTransaction)
    if (!mention || !mention.title || deleted || editor.isDestroyed) {
      return
    }
    const { state } = editor
    const linkType = state.schema.marks.link
    if (to > state.doc.content.size || state.doc.textBetween(from, to) !== url ||
        !state.doc.rangeHasMark(from, to, linkType)) {
      return
    }
    editor.chain().insertContentAt({ from, to }, {
      type: 'mention',
      attrs: { id: mention.id, label: mention.title, kind: page.kind, mentionSuggestionChar: '@' }
    }).run()
  }

  fetchPageMention(page.kind, page.id).then(finish, () => finish(null))
}
